import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  InputAdornment,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import {
  Add,
  Refresh,
  NavigateNext,
  Home,
  Close,
  Search,
  CardGiftcard,
  AttachMoney,
  Stars,
  Star,
  CheckCircle,
  StarBorder,
} from '@mui/icons-material'; // Added StarBorder here

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListRewardsQuery,
  useAddRewardMutation,
  useSetDefaultRewardMutation,
  Reward,
  RewardFormData,
  validateAmount,
  validatePoints,
} from '../../features/admin/rewardApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const RewardManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<RewardFormData>({
    reward_name: '',
    amount: '',
    points: '',
    is_default: false,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListRewardsQuery();

  const [addReward, { isLoading: isAdding }] = useAddRewardMutation();
  const [setDefaultReward, { isLoading: isSettingDefault }] = useSetDefaultRewardMutation();

  // Handle API response structure
  const rewards = data?.data || [];
  const defaultReward = rewards.find((reward: Reward) => reward.is_default === 1);

  const isFormLoading = isAdding;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      reward_name: '',
      amount: '',
      points: '',
      is_default: false,
    });
    setFormErrors({});
  };

  const handleAddReward = () => {
    resetForm();
    // Only pre-check default if there's no existing default reward
    setFormData(prev => ({
      ...prev,
      is_default: !defaultReward
    }));
    setFormOpen(true);
  };

  const handleSetDefault = async (reward: Reward) => {
    try {
      const result = await setDefaultReward({ id: reward.id }).unwrap();
      setSnackbar({
        open: true,
        message: result?.message || `${reward.reward_name} is now the default reward!`,
        severity: 'success'
      });
      refetch();
    } catch (err: any) {
      console.error('Failed to set default reward:', err);
      setSnackbar({
        open: true,
        message: err?.data?.message || 'Failed to set default reward. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate reward name - must not be empty and should have a proper name
    const rewardName = formData.reward_name.trim();
    if (!rewardName) {
      newErrors.reward_name = 'Reward name is required';
    } else if (rewardName.toLowerCase() === 'unnamed' || rewardName.toLowerCase() === 'unnamed reward') {
      newErrors.reward_name = 'Please enter a proper reward name';
    } else if (rewardName.length < 3) {
      newErrors.reward_name = 'Reward name must be at least 3 characters';
    }

    // Validate amount
    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (!validateAmount(formData.amount)) {
      newErrors.amount = 'Invalid amount (must be a positive number)';
    } else {
      const amountNum = parseFloat(formData.amount);
      if (amountNum <= 0) {
        newErrors.amount = 'Amount must be greater than 0';
      }
    }

    // Validate points
    if (!formData.points.trim()) {
      newErrors.points = 'Points are required';
    } else if (!validatePoints(formData.points)) {
      newErrors.points = 'Invalid points (must be a positive number)';
    } else {
      const pointsNum = parseFloat(formData.points);
      if (pointsNum <= 0) {
        newErrors.points = 'Points must be greater than 0';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const addData = {
        reward_name: formData.reward_name.trim(),
        amount: formData.amount.trim(),
        points: formData.points.trim(),
        is_default: formData.is_default,
      };
      
      const result = await addReward(addData).unwrap();
      setSnackbar({
        open: true,
        message: result?.message || 'Reward conversion created successfully!',
        severity: 'success'
      });
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save reward:', err);

      let errorMessage = 'Error creating reward conversion. Please try again.';

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view rewards.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No rewards found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load rewards. Please try again.';
      }
    }
    return 'Network error. Please check connection.';
  };

  // Filter rewards based on search
  const filteredRewards = rewards.filter((reward: Reward) => {
    const matchesSearch = !searchStr || 
      reward.reward_name?.toLowerCase().includes(searchStr.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Users
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Reward Conversions
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Reward Conversions
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Manage reward points and conversion rates {defaultReward && (
                    <Chip 
                      label={`Default: ${defaultReward.reward_name}`}
                      size="small"
                      icon={<Star sx={{ fontSize: 16 }} />}
                      sx={{ 
                        ml: 1,
                        backgroundColor: shopifyColors.gold + '20',
                        color: shopifyColors.textPrimary,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddReward}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add New Conversion
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search conversions..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredRewards.length} conversion{filteredRewards.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading conversions...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredRewards.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <CardGiftcard sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No conversions found' : 'No reward conversions yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No reward conversions match your search "${searchStr}"`
                    : 'Add your first reward conversion to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddReward}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add New Conversion
                  </Button>
                )}
              </Card>
            )}

            {/* Rewards List */}
            {!isLoading && filteredRewards.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredRewards.map((reward: Reward, index: number) => (
                    <ListItem
                      key={reward.id}
                      sx={{
                        borderBottom: index < filteredRewards.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                        position: 'relative',
                      }}
                    >
                      {/* Default indicator */}
                      {reward.is_default === 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: '4px',
                            backgroundColor: shopifyColors.gold,
                          }}
                        />
                      )}
                      
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: reward.is_default === 1 ? shopifyColors.gold : shopifyColors.primaryLight,
                            width: 48,
                            height: 48,
                          }}
                        >
                          {reward.is_default === 1 ? <Star /> : <CardGiftcard />}
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {reward.reward_name}
                            </Typography>
                            {reward.is_default === 1 && (
                              <Chip
                                label="Default"
                                size="small"
                                icon={<Star sx={{ fontSize: 14 }} />}
                                sx={{
                                  backgroundColor: shopifyColors.gold + '20',
                                  color: shopifyColors.textPrimary,
                                  fontWeight: 500,
                                  height: '22px',
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AttachMoney sx={{ fontSize: 16, color: shopifyColors.success }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Amount: ${parseFloat(reward.amount).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Stars sx={{ fontSize: 16, color: reward.is_default === 1 ? shopifyColors.gold : shopifyColors.purple }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Points: {reward.points}
                                </Typography>
                              </Box>
                              <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                Conversion: ${parseFloat(reward.amount).toFixed(2)} = {reward.points} points
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 0.5, display: 'block' }}>
                              ID: {reward.id} • Created: {new Date(reward.created_at || '').toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      {reward.is_default !== 1 && (
                        <Tooltip title="Set as Default">
                          <IconButton
                            onClick={() => handleSetDefault(reward)}
                            disabled={isSettingDefault}
                            size="small"
                            sx={{
                              color: shopifyColors.textSecondary,
                              '&:hover': {
                                color: shopifyColors.gold,
                              }
                            }}
                          >
                            <StarBorder />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              Add Reward Conversion
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Reward Name */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Reward Details
                </Typography>
                
                <TextField
                  name="reward_name"
                  label="Reward Name *"
                  value={formData.reward_name}
                  onChange={handleInputChange}
                  error={!!formErrors.reward_name}
                  helperText={formErrors.reward_name || "Enter a descriptive name (e.g., 'Basic Conversion', 'Premium Conversion')"}
                  fullWidth
                  required
                  placeholder="e.g., Basic Conversion, Premium Conversion"
                  disabled={isFormLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Amount and Points */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Conversion Rate
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="amount"
                      label="Amount ($) *"
                      value={formData.amount}
                      onChange={handleInputChange}
                      error={!!formErrors.amount}
                      helperText={formErrors.amount || "Monetary value in dollars"}
                      fullWidth
                      required
                      placeholder="1.00"
                      disabled={isFormLoading}
                      type="number"
                      inputProps={{ step: "0.01", min: "0" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoney sx={{ fontSize: 20, color: shopifyColors.textSecondary }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      name="points"
                      label="Points *"
                      value={formData.points}
                      onChange={handleInputChange}
                      error={!!formErrors.points}
                      helperText={formErrors.points || "Points equivalent"}
                      fullWidth
                      required
                      placeholder="100"
                      disabled={isFormLoading}
                      type="number"
                      inputProps={{ step: "1", min: "0" }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Stars sx={{ fontSize: 20, color: shopifyColors.gold }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 1, display: 'block' }}>
                  💡 Conversion: ${formData.amount || '0'} = {formData.points || '0'} points
                </Typography>
              </Box>

              <Divider />

              {/* Default Setting */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_default"
                      checked={formData.is_default || false}
                      onChange={handleSwitchChange}
                      disabled={isFormLoading}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                        Set as default reward conversion
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                        {defaultReward && formData.is_default
                          ? `⚠️ This will replace "${defaultReward.reward_name}" as the default conversion`
                          : 'This will be the default conversion rate used throughout the system'
                        }
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.reward_name?.trim() || !formData.amount?.trim() || !formData.points?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Adding...
                </Box>
              ) : (
                'Add Conversion'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardManagement;